import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import SubVisual from '../../components/SubVisual'
import LogoDefault from '../../assets/images/logo_default.png'
import LogoKorea from '../../assets/images/logo_korea.png'

const Company = () => {
	const { t } = useTranslation()
	const [visibleSections, setVisibleSections] = useState<string[]>([])

	// Intersection Observer 설정
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const sectionId = entry.target.getAttribute('data-section')
						if (sectionId) {
							setVisibleSections((prev) =>
								prev.includes(sectionId) ? prev : [...prev, sectionId],
							)
						}
					}
				})
			},
			{
				threshold: 0.2,
				rootMargin: '0px 0px -10% 0px',
			},
		)

		const sections = document.querySelectorAll('[data-section]')
		sections.forEach((section) => observer.observe(section))

		return () => observer.disconnect()
	}, [])

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900/20 to-gray-900 text-white overflow-x-hidden touch-action-manipulation">
			{/* 서브 비주얼 */}
			<SubVisual
				backgroundVideo="/videos/company.mp4"
				title={t('page.company.title')}
				subtitle={t('page.company.description')}
				height="h-[60vh]"
				overlayOpacity={0.6}
			/>
			{/* 회사 개요 섹션 */}
			{/* 회사 정보 섹션 */}
			<section
				data-section="company-info"
				className={`relative w-full py-24 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('company-info')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-950/20 to-transparent" />
				<div className="absolute inset-0 overflow-hidden">
					<div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" />
					<div
						className="absolute bottom-1/4 -right-48 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse"
						style={{ animationDelay: '1s' }}
					/>
				</div>

				<div className="relative container mx-auto max-w-5xl">
					<div className="text-center mb-16">
						<div className="inline-flex items-center px-6 py-3 bg-indigo-500/10 rounded-full border border-indigo-400/20 mb-6">
							<span className="text-indigo-300 text-sm font-medium tracking-wide">
								COMPANY INFORMATION
							</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-bold mb-8 bg-gradient-to-r from-white via-indigo-200 to-purple-200 text-transparent bg-clip-text">
							{t('page.company.inf')}
						</h2>
					</div>

					{/* 회사 정보 그리드 */}
					<div className="grid md:grid-cols-2 gap-8 lg:gap-12">
						{/* 일본 법인 카드 */}
						<div className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-indigo-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-indigo-500/20 hover:-translate-y-2">
							<div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-purple-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

							<div className="relative">
								<div className="mb-8 flex justify-center">
									<div className="bg-white rounded-xl p-6 border border-gray-200 group-hover:border-indigo-400 transition-all duration-300 shadow-lg">
										<img
											src={LogoDefault}
											alt="Mindcline Japan"
											className="h-24 w-auto object-contain"
										/>
									</div>
								</div>

								<div className="space-y-4">
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-indigo-400 font-semibold min-w-[80px]">
											{t('page.company.cl1')}
										</span>
										<span className="text-gray-300">{t('page.company.cl2')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-indigo-400 font-semibold min-w-[80px]">
											{t('page.company.cl3')}
										</span>
										<span className="text-gray-300">{t('page.company.cl4')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-indigo-400 font-semibold min-w-[80px]">
											{t('page.company.cl5')}
										</span>
										<span className="text-gray-300">{t('page.company.cl6')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-indigo-400 font-semibold min-w-[80px]">
											{t('page.company.cl7')}
										</span>
										<span className="text-gray-300">{t('page.company.cl8')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-indigo-400 font-semibold min-w-[80px]">
											{t('page.company.cl9')}
										</span>
										<span className="text-gray-300">{t('page.company.cl10')}</span>
									</div>
									<div className="flex items-start gap-3">
										<span className="text-indigo-400 font-semibold min-w-[80px]">
											{t('page.company.cl11')}
										</span>
										<span className="text-gray-300">{t('page.company.cl12')}</span>
									</div>
								</div>
							</div>
						</div>

						{/* 한국 법인 카드 */}
						<div className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2">
							<div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 to-pink-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

							<div className="relative">
								<div className="mb-8 flex justify-center">
									<div className="bg-white rounded-xl p-6 border border-gray-200 group-hover:border-purple-400 transition-all duration-300 shadow-lg">
										<img
											src={LogoKorea}
											alt="Mindcline Korea"
											className="h-24 w-auto object-contain"
										/>
									</div>
								</div>

								<div className="space-y-4">
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-purple-400 font-semibold min-w-[80px]">
											{t('page.company.cr1')}
										</span>
										<span className="text-gray-300">{t('page.company.cr2')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-purple-400 font-semibold min-w-[80px]">
											{t('page.company.cr3')}
										</span>
										<span className="text-gray-300">{t('page.company.cr4')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-purple-400 font-semibold min-w-[80px]">
											{t('page.company.cr5')}
										</span>
										<span className="text-gray-300">{t('page.company.cr6')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-purple-400 font-semibold min-w-[80px]">
											{t('page.company.cr7')}
										</span>
										<span className="text-gray-300">{t('page.company.cr8')}</span>
									</div>
									<div className="flex items-start gap-3 pb-3 border-b border-gray-700/50">
										<span className="text-purple-400 font-semibold min-w-[80px]">
											{t('page.company.cr9')}
										</span>
										<span className="text-gray-300">{t('page.company.cr10')}</span>
									</div>
									<div className="flex items-start gap-3">
										<span className="text-purple-400 font-semibold min-w-[80px]">
											{t('page.company.cr11')}
										</span>
										<span className="text-gray-300">{t('page.company.cr12')}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>{' '}
			{/* 비전 & 미션 섹션 */}
			<section
				data-section="vision-mission"
				className={`relative w-full py-24 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('vision-mission')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="relative container mx-auto">
					<div className="grid lg:grid-cols-2 gap-12">
						{/* 비전 */}
						<div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 backdrop-blur-sm p-10 rounded-3xl border border-indigo-400/30 hover:border-indigo-300/50 transition-all duration-500">
							<div className="flex items-center mb-6">
								<div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mr-4">
									<svg
										className="w-6 h-6 text-indigo-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<h3 className="text-3xl font-bold bg-gradient-to-r from-indigo-300 to-purple-300 text-transparent bg-clip-text">
									{t('page.company.vs1')}
								</h3>
							</div>
							<p className="text-lg text-gray-200 leading-relaxed">
								{t('page.company.vs2')}
								<br />
								{t('page.company.vs3')}
							</p>
						</div>

						{/* 미션 */}
						<div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 backdrop-blur-sm p-10 rounded-3xl border border-blue-400/30 hover:border-blue-300/50 transition-all duration-500">
							<div className="flex items-center mb-6">
								<div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mr-4">
									<svg
										className="w-6 h-6 text-blue-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M10 18l6-6-1.5-1.5L11 14V2H9v12l-3.5-3.5L4 12l6 6z" />
									</svg>
								</div>
								<h3 className="text-3xl font-bold bg-gradient-to-r from-blue-300 to-cyan-300 text-transparent bg-clip-text">
									{t('page.company.vs4')}
								</h3>
							</div>
							<p className="text-lg text-gray-200 leading-relaxed">
								{t('page.company.vs5')}
								<br />
								{t('page.company.vs6')}
							</p>
						</div>
					</div>
				</div>
			</section>
			{/* 핵심 가치 섹션 */}
			<section
				data-section="core-values"
				className={`relative w-full py-24 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('core-values')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="relative container mx-auto">
					<div className="text-center mb-16">
						<div className="inline-flex items-center px-6 py-3 bg-indigo-500/10 rounded-full border border-indigo-400/20 mb-6">
							<span className="text-indigo-300 text-sm font-medium tracking-wide">
								CORE VALUES
							</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 text-transparent bg-clip-text">
							{t('page.company.cv1')}
						</h2>
					</div>

					<div className="grid md:grid-cols-3 gap-8">
						{/* 혁신 */}
						<div className="group bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-500 hover:transform hover:scale-105">
							<div className="mb-6 group-hover:scale-110 transition-transform duration-300">
								<div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-8 h-8 text-yellow-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1a4 4 0 118 0v1a2 2 0 11-4 0v-1a2 2 0 10-4 0v1a2 2 0 11-4 0z" />
									</svg>
								</div>
							</div>
							<h3 className="text-2xl font-bold mb-4 text-white group-hover:text-indigo-300 transition-colors">
								Innovation
							</h3>
							<p className="text-gray-300 leading-relaxed">{t('page.company.cv2')}</p>
						</div>

						{/* 신뢰 */}
						<div className="group bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-500 hover:transform hover:scale-105">
							<div className="mb-6 group-hover:scale-110 transition-transform duration-300">
								<div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-8 h-8 text-green-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
									</svg>
								</div>
							</div>
							<h3 className="text-2xl font-bold mb-4 text-white group-hover:text-indigo-300 transition-colors">
								Trust
							</h3>
							<p className="text-gray-300 leading-relaxed">{t('page.company.cv3')}</p>
						</div>

						{/* 협력 */}
						<div className="group bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-500 hover:transform hover:scale-105">
							<div className="mb-6 group-hover:scale-110 transition-transform duration-300">
								<div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-8 h-8 text-blue-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.029 11H4.083a6.004 6.004 0 002.783 4.118z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
							</div>
							<h3 className="text-2xl font-bold mb-4 text-white group-hover:text-indigo-300 transition-colors">
								Collaboration
							</h3>
							<p className="text-gray-300 leading-relaxed">{t('page.company.cv4')}</p>
						</div>
					</div>
				</div>
			</section>
			{/* 연혁 섹션 */}
			<section
				data-section="history"
				className={`relative w-full py-24 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('history')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="relative container mx-auto">
					<div className="text-center mb-16">
						<div className="inline-flex items-center px-6 py-3 bg-indigo-500/10 rounded-full border border-indigo-400/20 mb-6">
							<span className="text-indigo-300 text-sm font-medium tracking-wide">
								HISTORY
							</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 text-transparent bg-clip-text">
							{t('page.company.hs1')}
						</h2>
					</div>

					{/* 타임라인 */}
					<div className="relative max-w-4xl mx-auto">
						{/* 중앙 라인 */}
						<div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-indigo-500/50 via-purple-500/50 to-indigo-500/50" />

						{/* 2025 */}
						<div className="relative mb-8">
							<div className="flex items-center justify-center mb-8">
								<div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center border-4 border-gray-900 shadow-lg shadow-indigo-500/50">
									<span className="text-xl font-bold">2025</span>
								</div>
							</div>
							<div className="grid md:grid-cols-2 gap-8">
								<div className="md:text-right bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-indigo-500/20">
									<h4 className="text-lg font-bold text-indigo-300 mb-2">2025.12.29</h4>
									<p className="text-gray-300">{t('page.company.hs5')}</p>
								</div>
								<div className="md:text-left bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-indigo-500/20">
									<h4 className="text-lg font-bold text-indigo-300 mb-2">2025.12.30</h4>
									<p className="text-gray-300">{t('page.company.hs6')}</p>
								</div>
							</div>
						</div>

						<div className="relative mb-8">
							<div className="flex items-center justify-center mb-0"></div>
							<div className="grid md:grid-cols-2 gap-8">
								<div className="md:text-right bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-indigo-500/20">
									<h4 className="text-lg font-bold text-indigo-300 mb-2">2025.09.25</h4>
									<p className="text-gray-300">{t('page.company.hs3')}</p>
								</div>
								<div className="md:text-left bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-indigo-500/20">
									<h4 className="text-lg font-bold text-indigo-300 mb-2">2025.10.02</h4>
									<p className="text-gray-300">{t('page.company.hs4')}</p>
								</div>
							</div>
						</div>

						<div className="relative mb-16">
							<div className="flex items-center justify-center mb-8">
								<div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center border-4 border-gray-900 shadow-lg shadow-indigo-500/50">
									<span className="text-xl font-bold">2024</span>
								</div>
							</div>
							<div className="grid md:grid-cols-2 gap-8">
								<div className="md:text-right bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-indigo-500/20">
									<h4 className="text-lg font-bold text-indigo-300 mb-2">2024.11.15</h4>
									<p className="text-gray-300">{t('page.company.hs2')}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
			{/* 인증 및 수상 섹션 */}
			<section
				data-section="certifications"
				className={`relative w-full py-24 px-6 lg:px-12 xl:px-20 transition-all duration-1000 ${
					visibleSections.includes('certifications')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-950/20 to-transparent" />

				<div className="relative container mx-auto">
					<div className="text-center mb-16">
						<div className="inline-flex items-center px-6 py-3 bg-indigo-500/10 rounded-full border border-indigo-400/20 mb-6">
							<span className="text-indigo-300 text-sm font-medium tracking-wide">
								CERTIFICATIONS & AWARDS
							</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 text-transparent bg-clip-text">
							{t('page.company.auth1')}
						</h2>
					</div>

					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
						{/* 인증 5 */}
						<div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-300 hover:transform hover:scale-105">
							<div className="mb-4">
								<div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-6 h-6 text-pink-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
								</div>
							</div>
							<h3 className="text-xl font-bold mb-2 text-white">
								{t('page.company.auth1_1')}
							</h3>
							<p className="text-gray-300">{t('page.company.auth1_2')}</p>
						</div>
						{/* 인증 1 */}
						<div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-300 hover:transform hover:scale-105">
							<div className="mb-4">
								<div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-6 h-6 text-yellow-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
								</div>
							</div>
							<h3 className="text-xl font-bold mb-2 text-white">
								{t('page.company.auth2_1')}
							</h3>
							<p className="text-gray-300">{t('page.company.auth2_2')}</p>
						</div>

						{/* 인증 2 */}
						<div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-300 hover:transform hover:scale-105">
							<div className="mb-4">
								<div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-6 h-6 text-indigo-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
							</div>
							<h3 className="text-xl font-bold mb-2 text-white">
								{t('page.company.auth3_1')}
							</h3>
							<p className="text-gray-300">{t('page.company.auth3_2')}</p>
						</div>

						{/* 인증 3 */}
						<div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-300 hover:transform hover:scale-105">
							<div className="mb-4">
								<div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-6 h-6 text-purple-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
								</div>
							</div>
							<h3 className="text-xl font-bold mb-2 text-white">
								{t('page.company.auth4_1')}
							</h3>
							<p className="text-gray-300">{t('page.company.auth4_2')}</p>
						</div>

						{/* 인증 4 */}
						<div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm p-8 rounded-2xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-300 hover:transform hover:scale-105">
							<div className="mb-4">
								<div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
									<svg
										className="w-6 h-6 text-orange-400"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
							</div>
							<h3 className="text-xl font-bold mb-2 text-white">
								{t('page.company.auth5_1')}
							</h3>
							<p className="text-gray-300">{t('page.company.auth5_2')}</p>
						</div>
					</div>
				</div>
			</section>
			{/* 파트너십 섹션 */}
			<section
				data-section="partnerships"
				className={`relative w-full py-24 px-6 lg:px-12 xl:px-20 mb-20 transition-all duration-1000 ${
					visibleSections.includes('partnerships')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-12'
				}`}
			>
				<div className="relative container mx-auto">
					<div className="text-center mb-16">
						<div className="inline-flex items-center px-6 py-3 bg-indigo-500/10 rounded-full border border-indigo-400/20 mb-6">
							<span className="text-indigo-300 text-sm font-medium tracking-wide">
								PARTNERSHIPS
							</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-indigo-200 to-purple-200 text-transparent bg-clip-text">
							{t('page.company.pt1')}
						</h2>
						<p className="text-lg text-gray-300 max-w-2xl mx-auto">
							{t('page.company.pt2')}
						</p>
					</div>

					{/* 파트너 로고 그리드 */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8">
						{[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
							<div
								key={item}
								className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm p-8 rounded-xl border border-indigo-500/20 hover:border-indigo-400/40 transition-all duration-300 hover:transform hover:scale-105 flex items-center justify-center aspect-square"
							>
								<div className="text-gray-400 text-sm font-medium">Partner {item}</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</div>
	)
}

export default Company
